import { useEffectEvent, useLayoutEffect, useRef, useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import type { StreamMessage } from "../../hooks/useStreamingText";

export function useLiveActivity(
  entry: ChatEntryWithSubTables | undefined,
  stream: StreamMessage | undefined,
  live: boolean
) {
  const activityRef = useRef<HTMLDivElement>(null);
  const [displayed, setDisplayed] = useState({
    entry,
    stream,
    entering: false,
  });
  const fading =
    live &&
    !!displayed.entry &&
    (displayed.entry.id !== entry?.id ||
      (displayed.entry.toolCall?.completedAt ?? null) !==
        (entry?.toolCall?.completedAt ?? null));

  if (!fading && (displayed.entry !== entry || displayed.stream !== stream)) {
    setDisplayed({ entry, stream, entering: live && displayed.entering });
  }

  const phase = fading ? "exit" : live && displayed.entering ? "enter" : null;
  const showLatest = useEffectEvent((entering: boolean) =>
    setDisplayed({ entry, stream, entering })
  );

  useLayoutEffect(() => {
    if (!phase) return;
    const element = activityRef.current;
    if (
      !element?.animate ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      showLatest(false);
      return;
    }

    const animation = element.animate(
      phase === "exit"
        ? [
            { opacity: 1, transform: "translateY(0)" },
            { opacity: 0, transform: "translateY(-4px)" },
          ]
        : [
            { opacity: 0, transform: "translateY(4px)" },
            { opacity: 1, transform: "translateY(0)" },
          ],
      {
        duration: phase === "exit" ? 100 : 120,
        easing: "ease-out",
        fill: "forwards",
      }
    );
    // Read the latest activity when the fade finishes so rapid updates do not queue.
    animation.onfinish = () => showLatest(phase === "exit");
    return () => {
      animation.onfinish = null;
      animation.cancel();
    };
  }, [phase]);

  return { displayed, fading, activityRef };
}
